import { type ReactNode, useEffect, useState } from 'react'
import { RouteGuard } from '@components/RouteGuard'
import { Sidenav, type SidenavUser } from '@components/Sidenav'
import { Topbar } from '@components/Topbar'
import { useDisplayName } from '@features/account/hooks/useDisplayName'
import { sessionStore } from '@features/auth/services/sessionStore'
import { crumbsFor } from '@utils/route'

/**
 * 認証済シェル: Sidenav + Topbar + page content。
 *
 * RouteGuard を内蔵し、未認証で保護ルートに来た場合は #login に強制 navigate。
 * `data-shell="main"` で theme.css の通常 grid (nav-w + 1fr) を適用する。
 *
 * Sidenav 識別領域 (avatar / 名前 / email) は `sessionStore.getMaterial()` から
 * 取得 (B-1)。`name` は backend API が現状 prelogin/login で返さないため email の
 * `@` 前を fallback。`initial` も email 先頭 1 文字 (大文字化) を fallback。
 * 将来 API が `User.name` を返すよう拡張された段階で sessionStore.material に
 * `name?: string` を追加し、ここで優先採用する。
 */

/** SessionStore material から Sidenav 表示用の user を組み立てる。null は未認証 / clear 直後。 */
const deriveSidenavUser = (): SidenavUser | undefined => {
  const material = sessionStore.getMaterial()
  if (!material) return undefined

  const email = material.email
  const localPart = email.split('@')[0] ?? email
  const initial = (localPart[0] ?? '').toUpperCase()

  return {
    email,
    name: localPart,
    initial,
  }
}

export interface MainLayoutProps {
  route: string
  go: (target: string) => void
  isAuthenticated: boolean

  /** LOCKED 状態かどうか。RouteGuard 経由で `#lock` 誘導に使う (L-18a)。 */
  isLocked: boolean
  children: ReactNode
  /** Topbar 検索 input の現在値 (T-E1)。未指定なら Topbar の検索 box は disabled */
  searchQuery?: string
  /** Topbar 検索 input の onChange + Enter 確定通知 (T-E1)。App.tsx で route 単位に state を持つ */
  onSearchChange?: (query: string) => void
}

export const MainLayout = ({
  route,
  go,
  isAuthenticated,
  isLocked,
  children,
  searchQuery,
  onSearchChange,
}: MainLayoutProps): JSX.Element => {
  // sessionStore 変化に追従 (login / lock / unlock / logout で material が変わる可能性)
  const [sidenavUser, setSidenavUser] = useState<SidenavUser | undefined>(() => deriveSidenavUser())
  // Topbar のサイドバートグルボタン (panel-left icon) で開閉する
  const [navCollapsed, setNavCollapsed] = useState(false)

  useEffect(() => {
    setSidenavUser(deriveSidenavUser())

    return sessionStore.subscribe(() => {
      setSidenavUser(deriveSidenavUser())
    })
  }, [])

  // accountProfileStore 経由で profile.name を優先表示する。fetch 失敗時は
  // email local part の fallback がそのまま継続。
  const displayName = useDisplayName()

  const sidenavUserWithName: SidenavUser | undefined =
    sidenavUser && displayName
      ? {
          ...sidenavUser,
          name: displayName,
          initial: (displayName[0] ?? sidenavUser.initial).toUpperCase(),
        }
      : sidenavUser

  // ナビ選択時: モバイルではドロワー (data-collapsed) を閉じる。デスクトップは
  // 折りたたみ状態を尊重するため何もしない。
  const handleNavigate = (target: string): void => {
    go(target)
    if (window.matchMedia('(max-width: 768px)').matches) {
      setNavCollapsed(false)
    }
  }

  return (
    <RouteGuard route={route} isAuthenticated={isAuthenticated} isLocked={isLocked}>
      <div className="app" data-shell="main" data-collapsed={navCollapsed ? 'true' : undefined}>
        <Sidenav route={route} go={handleNavigate} user={sidenavUserWithName} />
        {/* モバイル: ドロワー背後のスクリム。クリックでドロワーを閉じる。 */}
        <div className="nav-backdrop" aria-hidden="true" onClick={() => setNavCollapsed(false)} />
        <main className="main">
          <Topbar
            crumbs={crumbsFor(route)}
            searchQuery={searchQuery}
            onSearchChange={onSearchChange}
            onToggleNav={() => setNavCollapsed(v => !v)}
          />
          <div className="page">{children}</div>
        </main>
      </div>
    </RouteGuard>
  )
}
